import React, { useEffect, useRef, useState } from 'react';

const DEFAULT_ROOT_MARGIN = '0px 0px -10% 0px';
const DEFAULT_THRESHOLD = 0.08;

/**
 * Section animates in when it crosses the viewport (Intersection Observer).
 * Use `stagger` with child elements that have class `home-stagger-child` and optional `transitionDelay`.
 */
export default function ScrollRevealSection({
    children,
    className = '',
    style,
    revealVariant = 'fade-up',
    rootMargin = DEFAULT_ROOT_MARGIN,
    threshold = DEFAULT_THRESHOLD,
    stagger = false,
    disabled = false,
    ...rest
}) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (disabled) {
            setVisible(true);
            return undefined;
        }
        const node = ref.current;
        if (!node) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true);
            return undefined;
        }
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        obs.unobserve(entry.target);
                    }
                });
            },
            { root: null, rootMargin, threshold }
        );
        obs.observe(node);
        return () => obs.disconnect();
    }, [disabled, rootMargin, threshold]);

    const combinedClass = [
        'home-scroll-reveal',
        `home-scroll-reveal--${revealVariant}`,
        stagger ? 'home-scroll-reveal--has-stagger' : '',
        visible ? 'home-scroll-reveal--visible' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <section ref={ref} className={combinedClass} style={style} {...rest}>
            {children}
        </section>
    );
}
